import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ProjectsProvider } from './contexts/ProjectsContext';
import Cookies from 'js-cookie';
import { isManagementVerticalSession, isSalesVerticalSession } from './modules/shared/utils/authStorage';
import Dashboard from './modules/super-admin/components/layout/Dashboard';
import UserAdmin from './modules/super-admin/components/layout/UserAdmin';
import CompaniesList from './modules/builder/components/layout/CompaniesList';
import BillingPayments from './modules/super-admin/components/layout/BillingsPayments/BillingPayment';
import ClosedDeals from './modules/super-admin/components/layout/ClosedDeals';
import BuilderCompanies from './modules/super-admin/components/layout/BuilderCompanies';
import CompanyProjects from './modules/super-admin/components/layout/CompanyProjects';
import ProjectClosedDeals from './modules/super-admin/components/layout/ProjectClosedDeals';
// ... existing imports ...
import CompanyDetails from './modules/builder/pages/CompanyDetails';
import ProjectsList from './modules/builder/pages/ProjectsList';
import ProjectDetails from './modules/builder/pages/ProjectDetails';
import { Toaster } from 'react-hot-toast';
import UsersList from './modules/builder/pages/UsersList';
import RolePermission from './modules/builder/pages/RolePermission';
import UserDetails from './modules/builder/pages/UserDetails';
import Profile from './modules/builder/pages/Profile';
import LandDetails from './modules/builder/pages/LandDetails';
import LandsList from './modules/builder/pages/LandsList';
// import Register from './modules/shared/pages/Register';
import Login from './modules/shared/pages/Login';
import ForgotPassword from './modules/shared/pages/ForgotPassword';
// import VerifyEmail from './modules/shared/pages/VerifyEmail';
// import { SalesList } from './modules/builder/pages/SalesList';
// import { RateList } from './modules/builder/pages/RateList';
import SaleDetails from './modules/builder/pages/SaleDetails';
import VendorList from './modules/builder/pages/VendorList';
import VendorDetails from './modules/builder/pages/VendorDetails';
import ServiceProviderList from './modules/builder/pages/ServiceProviderList';
import MaterialList from './modules/builder/pages/MaterialList';
import Orders from './modules/builder/pages/Orders';
import RequirementList from './modules/builder/pages/RequirementList';
import RequirementDetails from './modules/builder/pages/RequirementDetails';
import SalesMain from './modules/builder/pages/SalesMain';
import TargetsAndIncentives from './modules/builder/pages/TargetsAndIncentives';
import PreleadListDetailPage from './modules/builder/pages/PreleadListDetailPage';
import PreleadList from './modules/builder/pages/PreleadList';
import AllPreleadLeadsPage from './modules/builder/pages/AllPreleadLeadsPage';
import PageNotFound from './modules/shared/pages/PageNotFound'; // Import the PageNotFound component
import OrdersRequirement from './modules/builder/pages/OrdersRequirement';
import LiveFeedList from './modules/builder/pages/LiveFeedList';
import AgreementFormNew from './modules/builder/pages/AgreementFormNew';
import LoginPhone from './modules/shared/pages/LoginPhone';
import CreatePasswordPage from './modules/shared/pages/CreatePassword';
import AgreementFormHandler from './modules/builder/pages/AgreementFormHandler';
import Analytics from './modules/builder/components/forms/Analytics';
import SuperAdminAnalytics from './modules/super-admin/components/forms/SuperAdminAnalytics';

import SalesReports from './modules/builder/pages/SalesReport';
import AccountsReport from './modules/builder/pages/AccountsReport';
import Integration from './modules/builder/pages/Integration';
import OrderItemsList from './modules/builder/pages/OrderItemsList';
import SentryTestPage from './modules/builder/pages/SentryTestPage';
import NewDocumentTemplate from './modules/builder/pages/NewDocumentTemplate';
import DocIntelTemplateStudio from './modules/builder/pages/DocIntelTemplateStudio';
import HomeRedirect from './modules/shared/components/layout/HomeRedirect';
import ServiceProviderDetails from './modules/builder/pages/ServiceProviderDetails';
import ChangeRequestsList from './modules/builder/pages/ChangeRequestsList';
import FacebookCompanies from './modules/builder/pages/FacebookCompanies';
import GoogleAdsCompanies from './modules/builder/pages/GoogleAdsCompanies';
import GoogleAdsLeadSources from './modules/builder/pages/GoogleAdsLeadSources';
import WebsiteForms from './modules/builder/pages/WebsiteForms';
import ZohoCompanies from './modules/builder/pages/ZohoCompanies';
import MailCompanies from './modules/builder/pages/MailCompanies';
import FBPages from './modules/builder/pages/FBPages';
import SinglePostOverView from './modules/builder/pages/SinglePostOverView';
import PartnersList from './modules/builder/pages/PartnersList';
import TaskTemplates from './modules/builder/pages/TaskTemplates';
import LiveTasks from './modules/builder/pages/LiveTasks';
import GanttChart from './modules/builder/components/ui/GanttChart/GanttChart.tsx';
import ConfigureHolidays from './modules/builder/pages/ConfigureHolidays';
import MarketingDashboard from './modules/builder/pages/MarketingDashboard';
import AddEditCampaign from './modules/builder/components/forms/AddEditCampaign';
import CampaignsList from './modules/builder/pages/CampaignsList';
import CampaignOverView from './modules/builder/pages/CampaignOverView';
import CampaignTemplates from './modules/builder/pages/CampaignTemplates';
import WhatsAppLeadCampaigns from './modules/builder/pages/WhatsAppLeadCampaigns';
import WhatsAppLeadFlowEditor from './modules/builder/pages/WhatsAppLeadFlowEditor';
import WhatsAppLeadQuestionnaire from './modules/builder/pages/WhatsAppLeadQuestionnaire';
import WhatsAppCampaignLayout from './modules/builder/components/layout/WhatsAppCampaignLayout';
import WhatsAppInbox from './modules/builder/pages/WhatsAppInbox';
import SalesLeadConversation from './modules/builder/pages/SalesLeadConversation';
import WhatsAppMediaLibrary from './modules/builder/pages/WhatsAppMediaLibrary';
import WhatsAppFlow from './modules/builder/pages/WhatsAppFlow';
import WhatsAppFlowEditor from './modules/builder/pages/WhatsAppFlowEditor';
import RequireBuilderWhatsappForCampaigns from './modules/builder/components/layout/RequireBuilderWhatsappForCampaigns';
import TemplateOne from './modules/builder/components/preSalesTemplates/TemplateOne';
import InvoicesList from './modules/builder/pages/InvoicesList';
import SalesDashboard from './modules/builder/pages/Sales/SalesDashboard';
import Performance from './modules/builder/pages/Performance';
import Leads from './modules/builder/pages/Sales/Leads';
import ClosedDealDetails from './modules/builder/pages/ClosedDealDetails';
import UnderConsiderationDetails from './modules/builder/pages/UnderConsdireationDetails';
import MainDashboard from './modules/builder/pages/Dashboard';
import EngineerTasks from './modules/builder/pages/Engineer/Tasks';
import Stock from './modules/builder/pages/Stock';
import Payables from './modules/builder/pages/Payables';
import PayableItems from './modules/builder/pages/PayableItems';
import PayablesHistory from './modules/builder/pages/PayablesHistory';
import MachineryList from './modules/builder/pages/MachineryList';
import DailyTasks from './modules/builder/pages/Inventory/DailyTasks';
import PerformaInvoiceList from './modules/builder/pages/PerformaInvoiceList';
import PerformaInvoiceHistory from './modules/builder/pages/PerformaInvoiceHistory';
import SalesPropertyTemplate from './modules/builder/pages/SalesPropertyTemplate';
import InvoiceDetails from './modules/builder/pages/InvoiceDetails';
import Priorities from './modules/builder/pages/Priorities';
import EngDashboard from './modules/builder/pages/Engineer/EngDashboard';
import TaskManagement from './modules/builder/pages/Engineer/TaskManagement';
import Enquries from './modules/super-admin/components/layout/Enquries';
import BuilderUnitStats from './modules/super-admin/components/layout/BuilderUnitStats';
import AiUsageDashboard from './modules/super-admin/components/layout/AiUsageDashboard';
import InventoryDashBoard from './modules/builder/pages/Inventory/InventoryDashboard';
import Configure from './modules/builder/pages/Management/Configure.jsx';
import ReviewMeeting from './modules/builder/pages/Sales/ReviewMeeting.jsx';
import ManagementPriorities from './modules/builder/pages/Management/Priorities.jsx';
import MeetingReport from './modules/builder/pages/Management/MeetingReport.jsx';
import SetTarget from './modules/builder/pages/Management/SetTarget.jsx';
import UniversalMeasurement from './modules/builder/pages/Management/UniversalMeasurement.jsx';
// import ManagementLiveTasks from './modules/builder/pages/Management/ManagementLiveTasks.jsx';
// import ManagementTaskDetails from './modules/builder/pages/Management/ManagementTaskDetails.jsx';
// import OperationsManagementTaskPage from './modules/builder/pages/OperationsManagementTaskPage';
// import OpsMgmtUpdates from './modules/builder/pages/OperationsManagementTaskUpdates';
import AccountsPaymentStages from './modules/builder/pages/AccountsPaymentStages';
import DailyReports from './modules/builder/pages/Engineer/reports/operationdailyreport/DailyReports';
import SampleDailyReport from './modules/builder/pages/Engineer/reports/operationdailyreport/SampleDailyReport';
import WeeklyReports from './modules/builder/pages/Engineer/reports/operationsweeklyreport/WeeklyReports';
import SampleWeeklyReport from './modules/builder/pages/Engineer/reports/operationsweeklyreport/SampleWeeklyReport';
import MonthlyReports from './modules/builder/pages/Engineer/reports/operationmonthlyreport/MonthlyReports';
import SampleMonthlyReport from './modules/builder/pages/Engineer/reports/operationmonthlyreport/SampleMonthlyReport';
import InventoryMonthlyReport from './modules/builder/pages/Inventory/inventorymonthlyreport/InventoryMonthlyReports';
import SampleInventoryMonthlyReport from './modules/builder/pages/Inventory/inventorymonthlyreport/SampleInventoryMonthlyReport';
import InventoryReport from './modules/builder/pages/Inventory/consumptionsreport/InventoryReport';
import EscalationReport from './modules/builder/pages/Inventory/escalationreport/EscalationReport';
import SalesUnitInventory from './modules/builder/pages/Sales/SalesUnitInventory';
import ProjectImages from './modules/builder/pages/Sales/ProjectImages';
import BlockUnitsImages from './modules/builder/pages/Sales/BlockUnitsImages';
import BlockUnitImages from './modules/builder/pages/Sales/BlockUnitImages';
import SalesReceivablesPage from './modules/builder/pages/Sales/SalesReceivablesPage';
import SalesClosedWonPage from './modules/builder/pages/Sales/SalesClosedWonPage';
import SalesDealsPage from './modules/builder/pages/Sales/SalesDealsPage';
import SalesClosedLostPage from './modules/builder/pages/Sales/SalesClosedLostPage';
import SalesUnderConsiderationPage from './modules/builder/pages/Sales/SalesUnderConsiderationPage';

// Guard: management admin routes are not for Sales vertical users (e.g. sales managers)
const SalesVerticalRouteGuard = ({ children }) => {
  if (isSalesVerticalSession()) {
    return <Navigate to="/builder/sales" replace />;
  }
  return children;
};

const guardManagementAdminRoute = (element) => (
  <SalesVerticalRouteGuard>{element}</SalesVerticalRouteGuard>
);

// Guard: operations daily/monthly reports are only for Management vertical
const ManagementOnlyReportGuard = ({ children }) => {
  const userVertical = Cookies.get('userVertical')?.toLowerCase();
  if (userVertical !== 'management') {
    return <Navigate to="/builder/operations/tasks" replace />;
  }
  return children;
};

/* 
  Router
*/

// Conditional component to render different sales components based on user vertical
const ConditionalSalesComponent = () => {
  const [userVertical, setUserVertical] = React.useState(null);

  React.useEffect(() => {
    const syncVertical = () => setUserVertical(Cookies.get('userVertical')?.toLowerCase() ?? '');
    syncVertical();
    window.addEventListener('userLoggedIn', syncVertical);
    window.addEventListener('sessionProfileSynced', syncVertical);
    return () => {
      window.removeEventListener('userLoggedIn', syncVertical);
      window.removeEventListener('sessionProfileSynced', syncVertical);
    };
  }, []);

  if (userVertical === null) {
    return <SalesMain />;
  }

  // Management vertical gets the cross-functional sales dashboard lens.
  // Sales vertical users (executives + managers like Chanpreet) use SalesMain.
  if (isManagementVerticalSession()) {
    return <SalesDashboard />;
  }

  return <SalesMain />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeRedirect />,
    errorElement: <PageNotFound homePath="/" />
  },
  {
    path: '/super-admin',
    children: [
      {
        path: '/super-admin/home',
        element: <Dashboard />
      },
      {
        path: '/super-admin/user-management',
        element: <UserAdmin />
      },
      {
        path: '/super-admin/user-management/users',
        element: <UserAdmin />
      },
      {
        path: '/super-admin/user-management/enquries',
        element: <Enquries />
      },
      {
        path: '/super-admin/user-management/unit-stats',
        element: <BuilderUnitStats />
      },
      {
        path: '/super-admin/user-management/ai-usage',
        element: <AiUsageDashboard />
      },
      {
        path: '/super-admin/closed-deals',
        element: <ClosedDeals />
      },
      {
        path: '/super-admin/closed-deals/:builderId/companies',
        element: <BuilderCompanies />
      },
      {
        path: '/super-admin/closed-deals/:builderId/companies/:companyId/projects',
        element: <CompanyProjects />
      },
      {
        path: '/super-admin/closed-deals/:builderId/companies/:companyId/projects/:projectId/deals',
        element: <ProjectClosedDeals />
      },
      {
        path: '/super-admin/billings-payment',
        element: <BillingPayments />
      },
      {
        path: '/super-admin/analytics',
        element: <SuperAdminAnalytics />
      },
      {
        path: '*',
        element: <PageNotFound homePath="/super-admin/home" /> // Catch-all route for undefined paths
      }
    ]
  },
  /* 
    Builder routes
  */

  {
    path: '/builder',
    children: [
      {
        path: '/builder/feed',
        element: <LiveFeedList />
      },
      {
        path: '/builder/dashboard',
        element: <MainDashboard />
      },
      {
        path: '/builder/management/configure',
        element: guardManagementAdminRoute(<Configure />)
      },
      {
        path: '/builder/management/universal-measurement',
        element: guardManagementAdminRoute(<UniversalMeasurement />)
      },
      {
        path: '/builder/management/priorities',
        element: guardManagementAdminRoute(<ManagementPriorities />)
      },
      {
        path: '/builder/management/review-meeting',
        element: guardManagementAdminRoute(<MeetingReport />)
      },
      {
        path: '/builder/management/set-target',
        element: guardManagementAdminRoute(<SetTarget />)
      },
      {
        path: '/builder/sales/reports',
        element: <ReviewMeeting />
      },
      {
        path: '/builder/priority/leads',
        element: <Leads />
      },
      {
        path: '/builder/management',
        element: guardManagementAdminRoute(<CompaniesList />)
      },
      {
        path: '/builder/management/company',
        element: guardManagementAdminRoute(<CompaniesList />)
      },
      {
        path: '/builder/management/company/:id',
        element: guardManagementAdminRoute(<CompanyDetails />)
      },
      {
        path: '/builder/billings-payment',
        element: <div>{`Builder module > Billings`}</div>
      },
      {
        path: '/builder/analytics',
        element: <Analytics />
      },
      {
        path: '/builder/management/land/:id',
        element: guardManagementAdminRoute(<LandDetails />)
      },
      {
        path: '/builder/management/land',
        element: guardManagementAdminRoute(<LandsList />)
      },
      {
        path: '/builder/management/partner',
        element: guardManagementAdminRoute(<PartnersList />)
      },
      {
        path: '/builder/management/company/:id/projects',
        element: guardManagementAdminRoute(<ProjectsList />)
      },
      {
        path: '/builder/management/company/projects/:id',
        element: guardManagementAdminRoute(<ProjectDetails />)
      },
      {
        path: '/builder/operations',
        element: <EngDashboard />
      },
      {
        path: '/builder/operations/dashboard',
        element: <EngDashboard />
      },
      {
        path: '/builder/operations/tasks',
        element: <LiveTasks />
      },
      {
        path: '/builder/operations/tasksUpdates',
        element: <EngineerTasks />
      },
      // {
      //   path: '/builder/operations/managementTasks',
      //   element: <OperationsManagementTaskPage />
      // },
      // {
      //   path: '/builder/operations/managementTasks/:id',
      //   element: <OpsMgmtUpdates />
      // },
      {
        path: '/builder/operations/daily-reports',
        element: (
            <DailyReports />
        )
      },
      {
        path: '/builder/operations/daily-reports/sample',
        element: (
            <SampleDailyReport />
        )
      },
      {
        path: '/builder/operations/weekly-reports',
        element: (
          <ManagementOnlyReportGuard>
            <WeeklyReports />
          </ManagementOnlyReportGuard>
        )
      },
      {
        path: '/builder/operations/weekly-reports/sample',
        element: (
          <ManagementOnlyReportGuard>
            <SampleWeeklyReport />
          </ManagementOnlyReportGuard>
        )
      },
      {
        path: '/builder/operations/monthly-reports',
        element: (
          <ManagementOnlyReportGuard>
            <MonthlyReports />
          </ManagementOnlyReportGuard>
        )
      },
      {
        path: '/builder/operations/monthly-reports/sample',
        element: (
          <ManagementOnlyReportGuard>
            <SampleMonthlyReport />
          </ManagementOnlyReportGuard>
        )
      },
      // TODO: uncomment when escalationreport is added
      // {
      //   path: '/builder/inventory/escalation-report',
      //   element: <EscalationReport />
      // },
      { path: '/builder/operations/configureholiday', element: <ConfigureHolidays /> },
      {
        path: '/builder/operations/change-requests',
        element: <ChangeRequestsList />
      },
      {
        path: '/builder/operations/templates',
        element: <TaskTemplates />
      },
      {
        path: '/builder/operations/tasks/ganttChart',
        element: <GanttChart />
      },
      {
        path: '/builder/operations/task-management',
        element: <TaskManagement />
      },
      {
        path: '/builder/operations/service-providers',
        element: <ServiceProviderList />
      },
      // {
      //   path: '/builder/operations/accommodation-blocks',
      //   element: <AccommodationBlocks />
      // },
      // {
      //   path: '/builder/operations/accommodation-blocks/boq/:accommodationId',
      //   element: <AccommodationBoqEditor />
      // },
      {
        path: '/builder/inventory',
        element: <InventoryDashBoard />
      },
      {
        path: '/builder/inventory/dashboard',
        element: <InventoryDashBoard />
      },
      {
        path: '/builder/inventory/vendors',
        element: <VendorList />
      },
      {
        path: '/builder/inventory/materials',
        element: <MaterialList />
      },
      {
        path: '/builder/inventory/requirements',
        element: <RequirementList />
      },
      {
        path: '/builder/inventory/requirements/:id',
        element: <RequirementDetails />
      },
      {
        path: '/builder/inventory/orders',
        element: <Orders />
      },
      {
        path: '/builder/inventory/monthly-report',
        element: <InventoryMonthlyReport />
      },
      {
        path: '/builder/inventory/monthly-report/sample',
        element: <SampleInventoryMonthlyReport />
      },
      {
        path: '/builder/inventory/consumption-report',
        element: <InventoryReport />
      },
      // {
      //   path: '/builder/inventory/managementTasks',
      //   element: <OperationsManagementTaskPage />
      // },
      // {
      //   path: '/builder/inventory/managementTasks/:id',
      //   element: <OpsMgmtUpdates />
      // },
      {
        path: '/builder/profile',
        element: <Profile />
      },
      {
        path: '/builder/users',
        element: guardManagementAdminRoute(<UsersList />)
      },
      {
        path: '/builder/users/roles',
        element: guardManagementAdminRoute(<RolePermission />)
      },
      {
        path: '/builder/users/:id',
        element: guardManagementAdminRoute(<UserDetails />)
      },
      {
        path: '/builder/vendors/:id',
        element: <VendorDetails />
      },
      {
        path: '/builder/serviceprovider/:id',
        element: <ServiceProviderDetails />
      },
      {
        path: '/builder/sales',
        element: <ConditionalSalesComponent />
      },
      // {
      //   path: '/builder/sales/dashboard',
      //   element: <SalesMain secondaryTab="Dashboard" />
      // },
      {
        path: '/builder/sales/performance',
        element: <Performance />
      },
      // {
      //   path: '/builder/sales/targets-incentives',
      //   element: <TargetsAndIncentives />
      // },
      {
        path: '/builder/sales/',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/receivables',
        element: <SalesReceivablesPage />
      },
      {
        path: '/builder/sales/closed-won',
        element: <SalesClosedWonPage />
      },
      {
        path: '/builder/sales/deals',
        element: <SalesDealsPage />
      },
      {
        path: '/builder/sales/closed-lost',
        element: <SalesClosedLostPage />
      },
      {
        path: '/builder/sales/under-consideration',
        element: <SalesUnderConsiderationPage />
      },
      {
        path: '/builder/sales/total-leads/hot',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/total-leads/warm',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/total-leads/cold',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/total-leads/nurture',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/total-leads/future-prospect',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/total-leads',
        element: <SalesMain />
      },
      {
        path: '/builder/sales/propertyTemplates',
        element: <SalesPropertyTemplate from={'sales'} />
      },
      // {
      //   path: '/builder/sales/unit-inventory',
      //   element: <SalesUnitInventory />
      // },
      // {
      //   path: '/builder/sales/unit-inventory/:id',
      //   element: <SalesUnitInventory />
      // },
      {
        path: '/builder/sales/project-images',
        element: <ProjectImages />
      },
      {
        path: '/builder/sales/project-images/:id',
        element: <ProjectImages />
      },
      {
        path: '/builder/sales/block-units-images',
        element: <BlockUnitsImages />
      },
      {
        path: '/builder/sales/block-units-images/:id/block/:blockId',
        element: <BlockUnitImages />
      },
      {
        path: '/builder/sales/block-units-images/:id',
        element: <BlockUnitsImages />
      },
      {
        path: '/builder/sales/prelead-lists/:listId',
        element: <PreleadListDetailPage />
      },
      {
        path: '/builder/sales/prelead-lists/all-leads',
        element: <AllPreleadLeadsPage />
      },
      {
        path: '/builder/sales/whatsapp/:phone',
        element: <SalesLeadConversation />
      },
      {
        path: '/builder/sales/:id',
        element: <SaleDetails />
      },
      {
        path: '/builder/accounts',
        element: <InvoicesList />
      },
      {
        path: '/builder/accounts/invoice',
        element: <InvoicesList />
      },

      {
        path: '/builder/accounts/payables',
        element: <Payables />
      },
      {
        path: '/builder/accounts/payables/:id',
        element: <PayableItems />
      },
      {
        path: '/builder/accounts/history',
        element: <PayablesHistory />
      },
      {
        path: '/builder/accounts/priorities',
        element: <Priorities />
      },
      {
        path: '/builder/accounts/payment-stages',
        element: <AccountsPaymentStages />
      },
      {
        path: '/builder/accounts/performa',
        element: <PerformaInvoiceList />
      },
      {
        path: '/builder/accounts/PIHistory',
        element: <PerformaInvoiceHistory />
      },
      {
        path: '/builder/post/:id',
        element: <SinglePostOverView />
      },
      {
        path: '/builder/deals/:id',
        // element: <DealsList />
        element: <ClosedDealDetails />
      },
      {
        path: '/builder/UCDetails/:id',
        // element: <DealsList />
        element: <UnderConsiderationDetails />
      },
      {
        path: '/builder/inventory/orders/requirement/:id',
        element: <OrdersRequirement />
      },
      {
        path: '/builder/Agreement/:id',
        element: <AgreementFormHandler />
      },
      {
        path: '/builder/Allotment/:id',
        element: <AgreementFormNew />
      },
      {
        path: '/builder/reports',
        element: <SalesReports />
      },
      {
        path: '/builder/reports/sales',
        element: <SalesReports />
      },
      {
        path: '/builder/reports/accounts',
        element: <AccountsReport />
      },
      {
        path: '/builder/marketing',
        element: <MarketingDashboard />
      },
      {
        path: '/builder/marketing/dashboard',
        element: <MarketingDashboard />
      },
      {
        path: '/builder/marketing/:type/campaign/:id?',
        element: <AddEditCampaign />
      },
      { path: '/builder/marketing/forms', element: <CampaignsList /> },
      { path: '/builder/marketing/forms/:name/:id/:project_id', element: <CampaignOverView /> },
      { path: '/builder/marketing/templates', element: <CampaignTemplates /> },
      {
        path: '/builder/marketing/whatsapp-flow',
        element: (
          <RequireBuilderWhatsappForCampaigns>
            <WhatsAppFlow />
          </RequireBuilderWhatsappForCampaigns>
        )
      },
      {
        path: '/builder/marketing/whatsapp-flow/:flowId',
        element: (
          <RequireBuilderWhatsappForCampaigns>
            <WhatsAppFlowEditor />
          </RequireBuilderWhatsappForCampaigns>
        )
      },
      {
        path: '/builder/marketing/whatsapp-templates',
        element: <Navigate to="/builder/marketing/whatsapp-flow" replace />
      },
      {
        path: '/builder/marketing/whatsapp-campaigns',
        element: (
          <RequireBuilderWhatsappForCampaigns>
            <WhatsAppCampaignLayout />
          </RequireBuilderWhatsappForCampaigns>
        ),
        children: [
          { index: true, element: <WhatsAppLeadCampaigns /> },
          { path: 'inbox', element: <WhatsAppInbox /> },
          { path: 'media', element: <WhatsAppMediaLibrary /> }
        ]
      },
      {
        path: '/builder/marketing/whatsapp-campaigns/:campaignId/questionnaire',
        element: (
          <RequireBuilderWhatsappForCampaigns>
            <WhatsAppLeadQuestionnaire />
          </RequireBuilderWhatsappForCampaigns>
        )
      },
      {
        path: '/builder/marketing/whatsapp-campaigns/:campaignId/flow',
        element: (
          <RequireBuilderWhatsappForCampaigns>
            <WhatsAppLeadFlowEditor />
          </RequireBuilderWhatsappForCampaigns>
        )
      },
      {
        path: '/builder/marketing/prelead-lists',
        element: <PreleadList />
      },
      {
        path: '/builder/marketing/prelead-lists/:listId',
        element: <PreleadListDetailPage />
      },
      {
        path: '/builder/marketing/prelead-lists/all-leads',
        element: <AllPreleadLeadsPage />
      },

      {
        path: '/builder/integration',
        element: <Integration />
      },
      {
        path: '/builder/integration/zoho-companies',
        element: <ZohoCompanies />
      },
      {
        path: '/builder/integration/facebook-companies',
        element: <FacebookCompanies />
      },
      {
        path: '/builder/integration/google-ads-companies',
        element: <GoogleAdsCompanies />
      },
      {
        path: '/builder/integration/google-ads-companies/lead-sources',
        element: <GoogleAdsLeadSources />
      },
      {
        path: '/builder/integration/website-forms',
        element: <WebsiteForms />
      },
      {
        path: '/builder/integration/facebook-companies/pages',
        element: <FBPages />
      },
      {
        path: '/builder/integration/mail-companies',
        element: <MailCompanies />
      },

      {
        path: '/builder/inventory/machinery',
        element: <MachineryList />
      },

      {
        path: '/builder/inventory/stock',
        element: <Stock />
      },
      {
        path: '/builder/inventory/dailytasks',
        element: <DailyTasks />
      },

      {
        path: '/builder/inventory/orders/items/:id',
        element: <OrderItemsList />
      },
      {
        path: '/builder/accounts/invoice/:id',
        element: <InvoiceDetails />
      },
      {
        path: '/builder/sentry',
        element: <SentryTestPage />
      },
      {
        path: '/builder/management/company/projects/project-documents/template/new/:id',
        element: guardManagementAdminRoute(<NewDocumentTemplate />)
      },
      {
        path: '/builder/management/company/projects/project-documents/ai-studio/:id',
        element: guardManagementAdminRoute(<DocIntelTemplateStudio />)
      },
      {
        path: '*',
        element: <PageNotFound homePath="/builder/feed" /> // Catch-all route for undefined paths
      }
    ]
  },
  /* 
    Auth routes
  */
  {
    path: '/auth',
    children: [
      // {
      //   path: '/auth/register',
      //   element: <Register />
      // },
      {
        path: '/auth/login',
        element: <Login />
      },
      {
        path: '/auth/login-phone',
        element: <LoginPhone />
      },
      // {
      //   path: '/auth/verify-email',
      //   element: <VerifyEmail />
      // },
      // {
      //   path: '/auth/create-password',
      //   element: <CreatePasswordPage />
      // },
      {
        path: '/auth/complete-new-password',
        element: <CreatePasswordPage />
      },
      {
        path: '/auth/forgot-password',
        element: <ForgotPassword />
      },
      // {
      //   path: '*',
      //   element: <PageNotFound homePath="/auth/login" />
      // },
      {
        path: '*',
        element: <PageNotFound homePath="/auth/login-phone" />
      }
    ]
  },
  {
    path: '*',
    element: <PageNotFound homePath="/" />
  },
  {
    path: '/:project/:campaign_id',
    element: <TemplateOne />
  }
]);

/* 
  React query client
*/
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 6000 * 1000
    }
  }
});

const App = () => {
  // const user = 'admin';
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ProjectsProvider>
          {/* <ReactQueryDevtools /> */}
          <Toaster position="top-right" />
          <RouterProvider router={router} />
        </ProjectsProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
